/**
 * Stock Audit List — "What counts have we done?"
 * Every stock count: when it started/ended, where, and how much was short / extra.
 * Expand any row to see the item-by-item detail (searchable + filterable).
 * Scoped to the branch selected in the header, like Inventory Balances.
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiClipboard, FiAlertCircle, FiTrendingDown, FiTrendingUp, FiDownload, FiPrinter,
  FiExternalLink, FiChevronRight, FiChevronDown, FiSearch,
} from 'react-icons/fi';
import PageLayout from '../../components/PageLayout';
import { useAuth } from '../../context/AuthContext';
import useCurrency from '../../hooks/useCurrency';
import useReportMemory from '../../hooks/useReportMemory';
import { colors } from '../../theme/colors';
import { loadAudits, loadAuditLines } from '../../utils/stockAuditHelper';
import {
  SummaryCards, SummaryCard, SummaryIcon, SummaryInfo, SummaryLabel, SummaryValue,
  FiltersCard, FiltersRow, FilterGroup, Label, Input, Select, ButtonGroup, Button,
  ContentCard, TableContainer, Table, Th, Td, TotalRow, EmptyState,
  Short, Extra, Chip, ExpandBtn, ParentRow, SubRow, SubHeadRow, SearchWrap, MiniNote,
  printReport, downloadCsv, csvCell, fmtDate, fmtDateTime, duration,
} from './auditReportStyles';

const StockAuditList = () => {
  const { activeCompany, activeBranch } = useAuth();
  // Backward memory: restore filters + loaded rows on Back (0 re-reads).
  const [savedMem, saveMemory] = useReportMemory('stock-audit-list');
  const { formatCurrency } = useCurrency();
  const navigate = useNavigate();
  const companyId = activeCompany?.id;

  const [audits, setAudits] = useState(() => savedMem?.audits ?? []);
  const [loading, setLoading] = useState(() => savedMem ? false : true);
  const [from, setFrom] = useState(() => savedMem?.from ?? '');
  const [to, setTo] = useState(() => savedMem?.to ?? '');
  const [location, setLocation] = useState(() => savedMem?.location ?? 'all');
  const [status, setStatus] = useState(() => savedMem?.status ?? 'all');
  // Item-level filters — applied to the expanded detail
  const [itemSearch, setItemSearch] = useState(() => savedMem?.itemSearch ?? '');
  const [itemType, setItemType] = useState(() => savedMem?.itemType ?? 'diff'); // diff | short | extra | all

  const [expanded, setExpanded] = useState(() => savedMem?.expanded ?? {});   // auditId -> true
  const [linesFor, setLinesFor] = useState(() => savedMem?.linesFor ?? {});   // auditId -> lines[]

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try { setAudits(await loadAudits(companyId)); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [companyId]);
  // Backward memory: on Back (snapshot present) SKIP ONLY the first auto-load so
  // restored audits are reused with 0 re-reads; a later company change re-fetches.
  const skipInitialLoad = useRef(!!savedMem);
  useEffect(() => {
    if (skipInitialLoad.current) { skipInitialLoad.current = false; return; }
    load();
  }, [load]);

  // Backward memory: keep the snapshot in sync (filters are display-only, and the
  // expanded rows + their loaded lines are restored too), so Back restores this
  // exact view with 0 re-reads.
  useEffect(() => {
    if (loading) return;
    saveMemory({
      audits, expanded, linesFor,
      from, to, location, status, itemSearch, itemType,
    });
  }, [loading, audits, expanded, linesFor, from, to, location, status,
      itemSearch, itemType, saveMemory]);

  const locationOptions = useMemo(
    () => [...new Set(audits.map(a => a.locationName).filter(Boolean))].sort(),
    [audits]
  );

  const rows = useMemo(() => audits.filter(a => {
    // Header branch scope — same rule as Inventory Balances.
    if (activeBranch && activeBranch.id !== 'all' && a.branchId !== activeBranch.id) return false;
    const d = fmtDate(a.startedAt);
    if (from && d < from) return false;
    if (to && d > to) return false;
    if (location !== 'all' && a.locationName !== location) return false;
    if (status !== 'all' && a.status !== status) return false;
    return true;
  }), [audits, activeBranch, from, to, location, status]);

  const totals = useMemo(() => rows.reduce((t, a) => {
    t.count++;
    t.withDiff += a.totals?.itemsWithVariance || 0;
    t.short += a.totals?.shortageValue || 0;
    t.extra += a.totals?.excessValue || 0;
    return t;
  }, { count: 0, withDiff: 0, short: 0, extra: 0 }), [rows]);

  /** Item-level filter used by every expanded row. */
  const filterLines = useCallback((lines) => {
    const q = itemSearch.trim().toLowerCase();
    return (lines || []).filter(l => {
      const v = Number(l.variance) || 0;
      if (itemType === 'diff' && Math.abs(v) <= 0.0001) return false;
      if (itemType === 'short' && v >= -0.0001) return false;
      if (itemType === 'extra' && v <= 0.0001) return false;
      if (q && !(`${l.itemName || ''} ${l.itemCode || ''} ${l.barcode || ''}`.toLowerCase().includes(q))) return false;
      return true;
    }).sort((a, b) => Math.abs(Number(b.variance) || 0) - Math.abs(Number(a.variance) || 0));
  }, [itemSearch, itemType]);

  const toggle = async (a) => {
    const open = !expanded[a.id];
    setExpanded(p => ({ ...p, [a.id]: open }));
    if (open && !linesFor[a.id]) {
      try {
        const ls = await loadAuditLines(companyId, a.id);
        setLinesFor(p => ({ ...p, [a.id]: ls }));
      } catch (e) { setLinesFor(p => ({ ...p, [a.id]: [] })); }
    }
  };

  const openVoucher = (type, id) => {
    if (!id) return;
    navigate(type === 'AS' ? '/store/add-stock' : '/store/reduce-stock', { state: { viewTransactionId: id } });
  };

  const handleExport = () => {
    let csv = 'Started,Ended,Took,Branch,Location,Status,Items Counted,Items With Difference,Stock Short,Stock Extra,Reduce Stock Voucher,Add Stock Voucher\n';
    rows.forEach(a => {
      csv += [
        csvCell(fmtDateTime(a.startedAt)), csvCell(fmtDateTime(a.submittedAt)), csvCell(duration(a.startedAt, a.submittedAt)),
        csvCell(a.branchName || ''), csvCell(a.locationName), csvCell(a.status),
        a.totals?.itemsCounted ?? 0, a.totals?.itemsWithVariance ?? 0,
        a.totals?.shortageValue ?? 0, a.totals?.excessValue ?? 0,
        csvCell(a.postedReduceStockNumber || ''), csvCell(a.postedAddStockNumber || ''),
      ].join(',') + '\n';
    });
    downloadCsv(`Stock_Audit_List_${new Date().toISOString().slice(0, 10)}.csv`, csv);
  };

  const handlePrint = () => {
    const body = rows.map(a => `<tr>
      <td>${fmtDateTime(a.startedAt)}</td><td>${fmtDateTime(a.submittedAt)}</td>
      <td>${duration(a.startedAt, a.submittedAt)}</td>
      <td>${a.branchName || '-'}</td><td>${a.locationName}</td><td>${a.status}</td>
      <td class="r">${a.totals?.itemsCounted ?? 0}</td>
      <td class="r">${a.totals?.itemsWithVariance ?? 0}</td>
      <td class="r short">${formatCurrency(a.totals?.shortageValue || 0)}</td>
      <td class="r extra">${formatCurrency(a.totals?.excessValue || 0)}</td>
    </tr>`).join('');
    printReport('Stock Audit List',
      `${activeCompany?.companyName || ''}${activeBranch?.branchName && activeBranch.id !== 'all' ? ' | Branch: ' + activeBranch.branchName : ''} | ${from || 'All'} to ${to || 'All'} | ${rows.length} count(s)`,
      `<table><thead><tr><th>Started</th><th>Ended</th><th>Took</th><th>Branch</th><th>Location</th><th>Status</th>
        <th class="r">Items</th><th class="r">With Difference</th><th class="r">Stock Short</th><th class="r">Stock Extra</th>
       </tr></thead><tbody>${body}</tbody>
       <tfoot><tr><td colspan="8">Total</td>
         <td class="r short">${formatCurrency(totals.short)}</td>
         <td class="r extra">${formatCurrency(totals.extra)}</td></tr></tfoot></table>`);
  };

  const COLS = 11;

  return (
    <PageLayout pageTitle="Stock Audit List" breadcrumb="Reports / Audit Reports / Stock Audit List">
      <SummaryCards>
        <SummaryCard>
          <SummaryIcon color={colors.primary.main}><FiClipboard /></SummaryIcon>
          <SummaryInfo><SummaryLabel>Counts Done</SummaryLabel><SummaryValue>{totals.count}</SummaryValue></SummaryInfo>
        </SummaryCard>
        <SummaryCard>
          <SummaryIcon color="#1565C0"><FiAlertCircle /></SummaryIcon>
          <SummaryInfo><SummaryLabel>Items With Difference</SummaryLabel><SummaryValue>{totals.withDiff}</SummaryValue></SummaryInfo>
        </SummaryCard>
        <SummaryCard>
          <SummaryIcon color={colors.error.main}><FiTrendingDown /></SummaryIcon>
          <SummaryInfo><SummaryLabel>Total Stock Short</SummaryLabel>
            <SummaryValue style={{ color: colors.error.main }}>{formatCurrency(totals.short)}</SummaryValue></SummaryInfo>
        </SummaryCard>
        <SummaryCard>
          <SummaryIcon color={colors.success.main}><FiTrendingUp /></SummaryIcon>
          <SummaryInfo><SummaryLabel>Total Stock Extra</SummaryLabel>
            <SummaryValue style={{ color: colors.success.main }}>{formatCurrency(totals.extra)}</SummaryValue></SummaryInfo>
        </SummaryCard>
      </SummaryCards>

      <FiltersCard>
        <FiltersRow>
          <FilterGroup><Label>From Date</Label><Input type="date" value={from} onChange={e => setFrom(e.target.value)} /></FilterGroup>
          <FilterGroup><Label>To Date</Label><Input type="date" value={to} onChange={e => setTo(e.target.value)} /></FilterGroup>
          <FilterGroup>
            <Label>Location</Label>
            <Select value={location} onChange={e => setLocation(e.target.value)}>
              <option value="all">All Locations</option>
              {locationOptions.map(l => <option key={l} value={l}>{l}</option>)}
            </Select>
          </FilterGroup>
          <FilterGroup>
            <Label>Status</Label>
            <Select value={status} onChange={e => setStatus(e.target.value)}>
              <option value="all">All</option>
              <option value="Counting">Counting</option>
              <option value="PendingApproval">Pending Approval</option>
              <option value="Approved">Approved</option>
              <option value="Rejected">Rejected</option>
              <option value="Cancelled">Cancelled</option>
            </Select>
          </FilterGroup>
        </FiltersRow>
        <FiltersRow style={{ marginTop: 14 }}>
          <FilterGroup style={{ minWidth: 240 }}>
            <Label>Search Item (inside a count)</Label>
            <SearchWrap>
              <FiSearch size={14} />
              <Input value={itemSearch} onChange={e => setItemSearch(e.target.value)} placeholder="Item name, code or barcode…" />
            </SearchWrap>
          </FilterGroup>
          <FilterGroup>
            <Label>Show Items</Label>
            <Select value={itemType} onChange={e => setItemType(e.target.value)}>
              <option value="diff">Only differences</option>
              <option value="short">Only short items</option>
              <option value="extra">Only extra items</option>
              <option value="all">All counted items</option>
            </Select>
          </FilterGroup>
          <ButtonGroup>
            <Button onClick={handleExport} disabled={!rows.length}><FiDownload /> CSV</Button>
            <Button onClick={handlePrint} disabled={!rows.length}><FiPrinter /> Print</Button>
          </ButtonGroup>
        </FiltersRow>
      </FiltersCard>

      <ContentCard>
        <TableContainer>
          {loading ? <EmptyState>Loading…</EmptyState>
            : rows.length === 0 ? <EmptyState>No stock counts found for these filters.</EmptyState> : (
              <Table>
                <thead><tr>
                  <Th style={{ width: 30 }} />
                  <Th>Started</Th><Th>Ended</Th><Th>Took</Th><Th>Branch</Th><Th>Location</Th><Th>Status</Th>
                  <Th align="right">Items</Th><Th align="right">With Difference</Th>
                  <Th align="right">Stock Short</Th><Th align="right">Stock Extra</Th><Th>Vouchers</Th>
                </tr></thead>
                <tbody>
                  {rows.map(a => {
                    const isOpen = !!expanded[a.id];
                    const lines = filterLines(linesFor[a.id]);
                    return (
                      <React.Fragment key={a.id}>
                        <ParentRow $expanded={isOpen} onClick={() => toggle(a)}>
                          <Td><ExpandBtn>{isOpen ? <FiChevronDown /> : <FiChevronRight />}</ExpandBtn></Td>
                          <Td>{fmtDateTime(a.startedAt)}</Td>
                          <Td>{a.submittedAt ? fmtDateTime(a.submittedAt) : '—'}</Td>
                          <Td>{duration(a.startedAt, a.submittedAt)}</Td>
                          <Td>{a.branchName || '—'}</Td>
                          <Td><b>{a.locationName}</b></Td>
                          <Td><Chip $s={a.status}>{a.status === 'PendingApproval' ? 'Pending Approval' : a.status}</Chip></Td>
                          <Td align="right">{a.totals?.itemsCounted ?? '—'}</Td>
                          <Td align="right">{a.totals?.itemsWithVariance ?? '—'}</Td>
                          <Td align="right">{a.totals ? <Short>{formatCurrency(a.totals.shortageValue)}</Short> : '—'}</Td>
                          <Td align="right">{a.totals ? <Extra>{formatCurrency(a.totals.excessValue)}</Extra> : '—'}</Td>
                          <Td onClick={e => e.stopPropagation()}>
                            {a.postedReduceStockNumber && (
                              <Button style={{ padding: '4px 8px', fontSize: 11, marginRight: 6 }}
                                onClick={() => openVoucher('RS', a.postedReduceStockId)}>
                                {a.postedReduceStockNumber} <FiExternalLink size={10} />
                              </Button>
                            )}
                            {a.postedAddStockNumber && (
                              <Button style={{ padding: '4px 8px', fontSize: 11 }}
                                onClick={() => openVoucher('AS', a.postedAddStockId)}>
                                {a.postedAddStockNumber} <FiExternalLink size={10} />
                              </Button>
                            )}
                            {!a.postedReduceStockNumber && !a.postedAddStockNumber && '—'}
                          </Td>
                        </ParentRow>

                        {isOpen && !linesFor[a.id] && (
                          <SubRow><Td colSpan={COLS + 1}><MiniNote>Loading items…</MiniNote></Td></SubRow>
                        )}

                        {isOpen && linesFor[a.id] && (
                          <>
                            <SubHeadRow>
                              <Td />
                              <Td colSpan={4}>Item</Td>
                              <Td>Code</Td>
                              <Td align="right">System</Td>
                              <Td align="right">Counted</Td>
                              <Td align="right">Difference</Td>
                              <Td align="right">Value</Td>
                              <Td />
                            </SubHeadRow>
                            {lines.length === 0 ? (
                              <SubRow><Td colSpan={COLS + 1}><MiniNote>No items match the item filter.</MiniNote></Td></SubRow>
                            ) : lines.map(l => {
                              const v = Number(l.variance) || 0;
                              return (
                                <SubRow key={l.itemId}>
                                  <Td />
                                  <Td colSpan={4}>
                                    {l.itemName}
                                    {l.notCounted && <Short style={{ marginLeft: 8, fontSize: 10 }}>NOT COUNTED</Short>}
                                  </Td>
                                  <Td>{l.itemCode}</Td>
                                  <Td align="right">{l.bookQty ?? 0}</Td>
                                  <Td align="right">{l.countedQty ?? 0}</Td>
                                  <Td align="right">
                                    {v < 0 ? <Short>{v}</Short> : v > 0 ? <Extra>+{v}</Extra> : <span style={{ color: '#999' }}>0</span>}
                                  </Td>
                                  <Td align="right">{formatCurrency(Math.abs(v) * (Number(l.unitCost) || 0))}</Td>
                                  <Td />
                                </SubRow>
                              );
                            })}
                          </>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
                <tfoot>
                  <TotalRow>
                    <Td colSpan={9} style={{ textAlign: 'right' }}>Total</Td>
                    <Td align="right"><Short>{formatCurrency(totals.short)}</Short></Td>
                    <Td align="right"><Extra>{formatCurrency(totals.extra)}</Extra></Td>
                    <Td />
                  </TotalRow>
                </tfoot>
              </Table>
            )}
        </TableContainer>
      </ContentCard>
    </PageLayout>
  );
};

export default StockAuditList;
